import React, { useState } from 'react';
import {
    Box, Typography, Paper, Grid, TextField, MenuItem, Button, Divider, IconButton, Card, CardContent, InputLabel, FormControl, Select, Container, RadioGroup, FormControlLabel, Radio, Checkbox, CircularProgress
} from '@mui/material';
import { ArrowForward, ArrowBack, Save as SaveIcon, CheckCircleOutline } from '@mui/icons-material';
import BackButton from "./BackButton";

const defaultState = {
    parentFeedback: {
        responseToName: 0,
        eyeContact: 0,
        expressNeeds: 0,
        understandInstructions: 0,
        socialInteraction: 0,
        speechClarity: 0,
        vocabularyImprovement: 0,
        comment: ""
    },
    receptiveLanguage: {
        respondsToName: 0,
        followsOneStep: 0,
        followsTwoStep: 0,
        identifiesObjects: 0,
        understandsBodyParts: 0,
        understandsWhQuestions: 0,
        comment: ""
    },
    expressiveLanguage: {
        usesSoundsWords: 0,
        usesGestures: 0,
        vocabularySize: 0,
        formsPhrases: 0,
        formsSentences: 0,
        namingObjects: 0,
        comment: ""
    },
    speechSkills: {
        soundProduction: 0,
        pronunciationClarity: 0,
        fluency: 0,
        voiceQuality: 0,
        comment: ""
    },
    socialCommunication: {
        initiatesCommunication: 0,
        maintainsConversation: 0,
        turnTaking: 0,
        appropriateExpressions: 0,
        comment: ""
    },
    behavioralAttention: {
        attentionSpan: 0,
        sittingTolerance: 0,
        compliance: 0,
        responseToTherapyCues: 0,
        comment: ""
    },
    oralMotor: {
        lipMovement: 0,
        tongueMovement: 0,
        jawControl: 0,
        blowingSucking: 0,
        comment: ""
    },
    playInteraction: {
        functionalPlay: 0,
        imitationSkills: 0,
        jointAttention: 0,
        comment: ""
    },
    summary: {
        totalScore: 0,
        previousScore: "",
        improvement: "No", // Yes, No, Moderate, Significant
        interpretation: "Age Appropriate", // Mild Delay, Moderate Delay, Severe Delay, Age Appropriate
        therapistObservation: "",
        shortTermGoals: "",
        longTermGoals: "",
        parentTrainingRequired: false,
        recommendedActivities: "",
        frequency: ""
    }
};

const scoreOptions = [
    { value: 0, label: "0 - No Change / None" },
    { value: 1, label: "1 - Slight / Emerging" },
    { value: 2, label: "2 - Moderate / Developing" },
    { value: 3, label: "3 - Significant / Achieved" }
];

const improvementOptions = ["No", "Yes", "Moderate", "Significant"];
const interpretationOptions = ["Age Appropriate", "Mild Delay", "Moderate Delay", "Severe Delay"];

const FormSectionCard = ({ title, children }) => (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '16px', mb: 3, overflow: 'visible', bgcolor: '#ffffff' }}>
        <Box sx={{ bgcolor: 'rgba(0, 188, 212, 0.05)', px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0097a7' }}>{title}</Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
            {children}
        </CardContent>
    </Card>
);

const SpeechReassessmentForm = ({ onSubmit, submitting }) => {
    const [data, setData] = useState(defaultState);
    const [step, setStep] = useState(0);

    const handleChange = (section, field, value) => {
        setData(prev => ({
            ...prev,
            [section]: { ...prev[section], [field]: value }
        }));
    };

    const handleNext = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setStep(prev => prev + 1);
    };
    
    const handlePrev = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setStep(prev => prev - 1);
    };

    const handleSubmitLocal = () => {
        // Calculate total score before submitting
        let total = 0;
        const sections = ['parentFeedback', 'receptiveLanguage', 'expressiveLanguage', 'speechSkills', 'socialCommunication', 'behavioralAttention', 'oralMotor', 'playInteraction'];
        sections.forEach(sec => {
            Object.keys(data[sec]).forEach(key => {
                if (typeof data[sec][key] === 'number') {
                    total += data[sec][key];
                }
            });
        });
        
        const finalData = { ...data };
        finalData.summary.totalScore = total;
        onSubmit(finalData);
    };

    const renderScoreInput = (label, section, field) => (
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2, p: 2, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Grid item xs={12} md={6}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{label}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined" size="small">
                    <Select
                        value={data[section][field]}
                        onChange={(e) => handleChange(section, field, e.target.value)}
                        sx={{ borderRadius: '12px', bgcolor: 'white' }}
                    >
                        {scoreOptions.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
    );

    const steps = [
        {
            title: "Foundation & Feedback",
            content: (
                <FormSectionCard title="2. Parent Feedback (Progress Since Last Assessment)">
                    {renderScoreInput("Response to name", "parentFeedback", "responseToName")}
                    {renderScoreInput("Eye contact", "parentFeedback", "eyeContact")}
                    {renderScoreInput("Ability to express needs", "parentFeedback", "expressNeeds")}
                    {renderScoreInput("Understanding instructions", "parentFeedback", "understandInstructions")}
                    {renderScoreInput("Social interaction", "parentFeedback", "socialInteraction")}
                    {renderScoreInput("Speech clarity", "parentFeedback", "speechClarity")}
                    {renderScoreInput("Vocabulary improvement", "parentFeedback", "vocabularyImprovement")}
                    <TextField
                        label="Parent Comments"
                        fullWidth
                        multiline
                        rows={2}
                        value={data.parentFeedback.comment}
                        onChange={(e) => handleChange("parentFeedback", "comment", e.target.value)}
                        sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                </FormSectionCard>
            )
        },
        {
            title: "Language Skills",
            content: (
                <>
                    <FormSectionCard title="3. Receptive Language Skills (Understanding)">
                        {renderScoreInput("Responds to name", "receptiveLanguage", "respondsToName")}
                        {renderScoreInput("Follows simple commands (1-step)", "receptiveLanguage", "followsOneStep")}
                        {renderScoreInput("Follows 2-step commands", "receptiveLanguage", "followsTwoStep")}
                        {renderScoreInput("Identifies objects", "receptiveLanguage", "identifiesObjects")}
                        {renderScoreInput("Understands body parts", "receptiveLanguage", "understandsBodyParts")}
                        {renderScoreInput("Understands WH questions", "receptiveLanguage", "understandsWhQuestions")}
                    </FormSectionCard>
                    <FormSectionCard title="4. Expressive Language Skills (Speaking)">
                        {renderScoreInput("Uses sounds/words", "expressiveLanguage", "usesSoundsWords")}
                        {renderScoreInput("Uses gestures", "expressiveLanguage", "usesGestures")}
                        {renderScoreInput("Vocabulary size", "expressiveLanguage", "vocabularySize")}
                        {renderScoreInput("Forms phrases", "expressiveLanguage", "formsPhrases")}
                        {renderScoreInput("Forms sentences", "expressiveLanguage", "formsSentences")}
                        {renderScoreInput("Naming objects", "expressiveLanguage", "namingObjects")}
                    </FormSectionCard>
                </>
            )
        },
        {
            title: "Speech & Social",
            content: (
                <>
                    <FormSectionCard title="5. Speech Skills (Articulation & Clarity)">
                        {renderScoreInput("Sound production", "speechSkills", "soundProduction")}
                        {renderScoreInput("Pronunciation clarity", "speechSkills", "pronunciationClarity")}
                        {renderScoreInput("Fluency", "speechSkills", "fluency")}
                        {renderScoreInput("Voice quality", "speechSkills", "voiceQuality")}
                    </FormSectionCard>
                    <FormSectionCard title="6. Social Communication Skills">
                        {renderScoreInput("Initiates communication", "socialCommunication", "initiatesCommunication")}
                        {renderScoreInput("Maintains conversation", "socialCommunication", "maintainsConversation")}
                        {renderScoreInput("Turn-taking", "socialCommunication", "turnTaking")}
                        {renderScoreInput("Uses appropriate expressions", "socialCommunication", "appropriateExpressions")}
                    </FormSectionCard>
                </>
            )
        },
        {
            title: "Behavior & Play",
            content: (
                <>
                    <FormSectionCard title="7. Behavioral & Attention Skills">
                        {renderScoreInput("Attention span", "behavioralAttention", "attentionSpan")}
                        {renderScoreInput("Sitting tolerance", "behavioralAttention", "sittingTolerance")}
                        {renderScoreInput("Compliance", "behavioralAttention", "compliance")}
                        {renderScoreInput("Response to therapy cues", "behavioralAttention", "responseToTherapyCues")}
                    </FormSectionCard>
                    <FormSectionCard title="9. Play & Interaction Skills">
                        {renderScoreInput("Functional play", "playInteraction", "functionalPlay")}
                        {renderScoreInput("Imitation skills", "playInteraction", "imitationSkills")}
                        {renderScoreInput("Joint attention", "playInteraction", "jointAttention")}
                    </FormSectionCard>
                </>
            )
        },
        {
            title: "Oral Motor Skills",
            content: (
                <FormSectionCard title="8. Oral Motor Skills">
                    {renderScoreInput("Lip movement", "oralMotor", "lipMovement")}
                    {renderScoreInput("Tongue movement", "oralMotor", "tongueMovement")}
                    {renderScoreInput("Jaw control", "oralMotor", "jawControl")}
                    {renderScoreInput("Blowing / sucking", "oralMotor", "blowingSucking")}
                </FormSectionCard>
            )
        },
        {
            title: "Clinical Summary",
            content: (
                <>
                    <FormSectionCard title="10 & 11. Scoring & Interpretation">
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Previous Total Score"
                                    fullWidth
                                    value={data.summary.previousScore}
                                    onChange={(e) => handleChange("summary", "previousScore", e.target.value)}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel>Overall Improvement</InputLabel>
                                    <Select
                                        value={data.summary.improvement}
                                        onChange={(e) => handleChange("summary", "improvement", e.target.value)}
                                        label="Overall Improvement"
                                        sx={{ borderRadius: '12px' }}
                                    >
                                        {improvementOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel>Clinical Interpretation</InputLabel>
                                    <Select
                                        value={data.summary.interpretation}
                                        onChange={(e) => handleChange("summary", "interpretation", e.target.value)}
                                        label="Clinical Interpretation"
                                        sx={{ borderRadius: '12px' }}
                                    >
                                        {interpretationOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </FormSectionCard>

                    <FormSectionCard title="12. Therapist Observation">
                        <TextField
                            label="Clinical Notes"
                            fullWidth
                            multiline
                            rows={4}
                            value={data.summary.therapistObservation}
                            onChange={(e) => handleChange("summary", "therapistObservation", e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                        />
                    </FormSectionCard>

                    <FormSectionCard title="13. Updated Therapy Goals">
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Short-Term Goals"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={data.summary.shortTermGoals}
                                    onChange={(e) => handleChange("summary", "shortTermGoals", e.target.value)}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Long-Term Goals"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={data.summary.longTermGoals}
                                    onChange={(e) => handleChange("summary", "longTermGoals", e.target.value)}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                />
                            </Grid>
                        </Grid>
                    </FormSectionCard>

                    <FormSectionCard title="14. Home Program Recommendations">
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={data.summary.parentTrainingRequired}
                                            onChange={(e) => handleChange("summary", "parentTrainingRequired", e.target.checked)}
                                        />
                                    }
                                    label="Parent training required"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Activities Suggested"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={data.summary.recommendedActivities}
                                    onChange={(e) => handleChange("summary", "recommendedActivities", e.target.value)}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Frequency"
                                    fullWidth
                                    value={data.summary.frequency}
                                    onChange={(e) => handleChange("summary", "frequency", e.target.value)}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                />
                            </Grid>
                        </Grid>
                    </FormSectionCard>
                </>
            )
        }
    ];

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <BackButton />
            </Box>

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, borderRadius: '16px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, justifyContent: 'space-between', borderBottom: '2px solid', borderColor: 'divider', pb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0097a7' }}>
                        {step + 1}. {steps[step].title}
                    </Typography>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                        Step {step + 1} of {steps.length}
                    </Typography>
                </Box>

                <Box sx={{ minHeight: '400px' }}>
                    {steps[step].content}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={handlePrev}
                        disabled={step === 0}
                        sx={{ borderRadius: '12px', px: 3 }}
                    >
                        Previous Step
                    </Button>
                    {step < steps.length - 1 ? (
                        <Button
                            variant="contained"
                            endIcon={<ArrowForward />}
                            onClick={handleNext}
                            sx={{ borderRadius: '12px', px: 4, bgcolor: '#00bcd4', '&:hover': { bgcolor: '#00acc1' } }}
                        >
                            Continue
                        </Button>
                    ) : (
                        <Button
                            disabled={submitting}
                            variant="contained"
                            color="success"
                            endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            onClick={handleSubmitLocal}
                            sx={{ borderRadius: '12px', px: 4 }}
                        >
                            {submitting ? "Saving..." : "Save Reassessment"}
                        </Button>
                    )}
                </Box>
            </Paper>
        </Container>
    );
};

export default SpeechReassessmentForm;
